import React, { useContext, useRef, useState } from "react";
import { useNewClientWizardContext } from "../Context/NewClientWizardContext";
import { AuthContext } from "../../../../../../context/AuthContext";
import styles from "../NewClientWizard.module.css";
import { FirmProfileData } from "../types";

/** Step 2 — firm profile (every PBIN database needs exactly one) plus signature image upload. */
export function FirmProfileStep({ clientName }: { clientName: string }) {
  const { formData, updateFormData, setError } = useNewClientWizardContext();
  const authContext = useContext(AuthContext) as any;
  const token: string = authContext.authState().accessToken.accessToken;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const update = (changes: Partial<FirmProfileData>) => {
    updateFormData({ firmProfile: { ...formData.firmProfile, ...changes } });
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const encodedClientName = encodeURIComponent(clientName || "client");
      const response = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/Provisioning/Images?clientName=${encodedClientName}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          mode: "cors",
          body,
        }
      );
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || "Upload failed.");
      }
      const result = await response.json();
      update({ primarySignorSignatureImagePath: result.path });
    } catch (err: any) {
      setError("Image upload failed: " + err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const text = (
    label: string,
    field: keyof FirmProfileData,
    required = false,
    placeholder = ""
  ) => (
    <div className={styles.fieldRow}>
      <label>{label}{required ? " *" : ""}</label>
      <input
        type="text"
        value={formData.firmProfile[field] as string}
        onChange={(e) => update({ [field]: e.target.value } as Partial<FirmProfileData>)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className={styles.stepBody}>
      <h4>Firm Profile</h4>
      {text("Company Name", "companyName", true)}
      {text("DTC Number", "dtcNo", true)}
      {text("Institution Number", "institutionNo")}
      {text("Tax ID", "taxId")}
      {text("Agent Bank Number", "agentBankNo")}
      {text("MPID", "mpid")}

      <h5>Contact</h5>
      {text("Title", "contactTitle")}
      {text("First Name", "contactFirstName")}
      {text("Last Name", "contactLastName")}
      {text("Department", "departmentName")}
      {text("Phone", "phoneNo")}
      {text("Fax (fax back to)", "faxNo")}
      {text("Email (email back to)", "emailBackTo")}

      <h5>Address</h5>
      {text("Address 1", "address1")}
      {text("Address 2", "address2")}
      {text("City", "city")}
      {text("State", "state")}
      {text("Zip", "zip")}

      <h5>Primary Signor Signature Image</h5>
      <div className={styles.fieldRow}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.gif"
          disabled={uploading}
          onChange={(e) => {
            const file = e.target.files && e.target.files[0];
            if (file) uploadImage(file);
          }}
        />
        {uploading && <span> Uploading…</span>}
      </div>
      {formData.firmProfile.primarySignorSignatureImagePath && (
        <p className={styles.hint}>
          Uploaded: {formData.firmProfile.primarySignorSignatureImagePath}{" "}
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => update({ primarySignorSignatureImagePath: "" })}
          >
            remove
          </button>
        </p>
      )}
    </div>
  );
}
